import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Input, Label } from "~/components/ui";
import type { JobListFilters } from "~/features/dbpjkitWorkerJobs/types";

interface DbpjkitWorkerJobsSearchFormProps {
  filters: JobListFilters;
  onApply: (next: JobListFilters) => void;
  onClear: () => void;
}

/**
 * Two-field text filter (command name + spreadsheet id) with Apply / Clear All
 * (V1 parity). Apply commits the draft; Clear resets both fields and the applied
 * filters. The draft re-seeds whenever the committed filters change from outside.
 */
export function DbpjkitWorkerJobsSearchForm({ filters, onApply, onClear }: DbpjkitWorkerJobsSearchFormProps) {
  const { t } = useTranslation();
  const [commandName, setCommandName] = useState(filters.commandName);
  const [spreadsheetId, setSpreadsheetId] = useState(filters.spreadsheetId);

  const prevFiltersRef = useRef(filters);
  if (prevFiltersRef.current !== filters) {
    prevFiltersRef.current = filters;
    setCommandName(filters.commandName);
    setSpreadsheetId(filters.spreadsheetId);
  }

  return (
    <form
      className="flex flex-col gap-3 md:flex-row md:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        onApply({ commandName, spreadsheetId });
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.dbpjkitWorkerJob.commandName")}</Label>
        <Input value={commandName} onChange={(e) => setCommandName(e.target.value)} className="md:w-60" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.dbpjkitWorkerJob.googleSpreadsheetID")}</Label>
        <Input value={spreadsheetId} onChange={(e) => setSpreadsheetId(e.target.value)} className="md:w-60" />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm">
          {t("ui.apply")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setCommandName("");
            setSpreadsheetId("");
            onClear();
          }}
        >
          {t("ui.clearAll")}
        </Button>
      </div>
    </form>
  );
}
